import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useTimeEntries } from '../../../core/hooks/useTimeEntries';
import { useAuth } from '../../../core/contexts/AuthContext';
import type { InvoiceItem } from '../../../core/models/Invoice';
import { format } from 'date-fns';
import { Clock } from 'lucide-react';

interface GenerateFromTimeDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (items: InvoiceItem[]) => void;
  contractId?: string;
}

export const GenerateFromTimeDialog: React.FC<GenerateFromTimeDialogProps> = ({
  open,
  onClose,
  onGenerate,
  contractId,
}) => {
  const { organizationId } = useAuth();
  const { timeEntries } = useTimeEntries({
    organizationId,
    contractId: contractId || undefined,
    billable: true,
  });
  const availableTimeEntries = timeEntries.filter(
    (entry) => entry.status !== 'billed' && entry.status !== 'rejected'
  );

  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());

  const handleToggleEntry = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEntries.size === availableTimeEntries.length) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(availableTimeEntries.map((e) => e.id)));
    }
  };

  const handleGenerate = () => {
    const selected = availableTimeEntries.filter((e) => selectedEntries.has(e.id));
    
    // Group by service and calculate totals
    const itemsMap = new Map<string, InvoiceItem>();
    
    selected.forEach((entry) => {
      const hours = entry.duration / 60;
      const key = entry.serviceId || 'general';
      
      if (itemsMap.has(key)) {
        const item = itemsMap.get(key)!;
        item.quantity += hours;
        item.amount = item.quantity * item.rate;
      } else {
        itemsMap.set(key, {
          description: `Service work - ${format(new Date(entry.startTime), 'MMM d')}`,
          quantity: hours,
          rate: 100, // Default rate - should come from service
          amount: hours * 100,
          timeEntryIds: [entry.id],
          serviceId: entry.serviceId,
        });
      }
    });

    const items = Array.from(itemsMap.values());
    onGenerate(items);
    setSelectedEntries(new Set());
    onClose();
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Generate Invoice from Time Entries</DialogTitle>
          <DialogDescription>
            Select billable, unbilled time entries to convert into invoice line items
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {availableTimeEntries.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              No billable, unbilled time entries found
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between border-b pb-2">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  {selectedEntries.size === availableTimeEntries.length ? 'Deselect All' : 'Select All'}
                </Button>
                <span className="text-sm text-gray-600">
                  {selectedEntries.size} of {availableTimeEntries.length} selected
                </span>
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {availableTimeEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 border rounded-lg p-3 hover:bg-gray-50"
                  >
                    <Checkbox
                      checked={selectedEntries.has(entry.id)}
                      onCheckedChange={() => handleToggleEntry(entry.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{entry.description}</p>
                      <div className="text-sm text-gray-600 mt-1 space-y-1">
                        <p>
                          <Clock className="inline h-3 w-3 mr-1" />
                          {formatDuration(entry.duration)} •{' '}
                          {format(new Date(entry.startTime), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={selectedEntries.size === 0}
          >
            Generate Invoice Items ({selectedEntries.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
